import {
    Admin,
    CustomRoutes,
    ListGuesser,
    RaThemeOptions,
    Resource,
    bwDarkTheme,
    bwLightTheme,
    localStorageStore,
} from 'react-admin';
import type { AdminProps, AuthProvider, DataProvider } from 'react-admin';
import { deepmerge } from '@mui/utils';
import { Route } from 'react-router';
import { ForgotPasswordPage, SetPasswordPage } from 'ra-supabase';

import { AccessDeniedPage } from '../layout/AccessDeniedPage';
import { Layout } from '../layout/Layout';
import { i18nProvider } from './i18nProvider';
import animation from '../animation';
import supply from '../supply';
import antennas from '../antenna';
import member from '../member';
import { Dashboard } from '../dashboard/Dashboard';
import mission from '../mission';
import tasks from '../tasks';
import formation from '../formation';
import expensesClaim from '../expenses_claim';
import highlightedResource from '../highlighted_resource';
import missionType from '../mission_type';
import studyForm from '../study_form';
import organization from '../organization';
import { CloudSyncPage } from '../cloud_sync';
import { LoginPage } from '../login/LoginPage';
import { SignupPage } from '../login/SignupPage';
import {
    authProvider as defaultAuthProvider,
    dataProvider as defaultDataProvider,
} from '@core/providers';
import {
    ConfigurationContextValue,
    ConfigurationProvider,
} from '@app/root/ConfigurationContext';
import {
    defaultAntennaTypes,
    defaultMemberGenderLabels,
    defaultMissionPipelineStatuses,
    defaultMissionStatusLabels,
    defaultMemberStatusLabels,
    defaultTaskTypeLabels,
    defaultMemberTypeLabels,
    defaultMissionVisibilityLabels,
    defaultVolunteerMissionAffinityLabels,
    defaultResourceTypeLabels,
} from '@app/root/defaultConfiguration';

// Define the interface for the CRM component props
export type CRMProps = {
    dataProvider?: DataProvider;
    authProvider?: AuthProvider;
    lightTheme?: RaThemeOptions;
    darkTheme?: RaThemeOptions;
} & Partial<ConfigurationContextValue> &
    Partial<AdminProps>;

const componentsTheme = {
    RaFileInput: {
        styleOverrides: {
            root: {
                '& .RaFileInput-dropZone': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
            },
        },
    },
    RaSidebar: {
        styleOverrides: {
            root: {
                '& .MuiPaper-root': {
                    maxWidth: 200,
                },
            },
        },
    },
};

const defaultLightTheme = deepmerge(bwLightTheme, {
    palette: {
        primary: {
            main: '#3F8543',
        },
        map: {
            popup: {
                background: bwLightTheme.palette?.common?.white,
            },
        },
    },
    components: componentsTheme,
});

const defaultDarkTheme = deepmerge(bwDarkTheme, {
    palette: {
        primary: {
            main: '#3F8543',
        },
        map: {
            popup: {
                background: bwDarkTheme.palette?.grey?.[800],
            },
        },
    },
    components: componentsTheme,
});

export const CRM = ({
    country = 'fr',
    language = 'en',
    memberGenderLabels = defaultMemberGenderLabels,
    antennaTypes = defaultAntennaTypes,
    missionPipelineStatuses = defaultMissionPipelineStatuses,
    missionStatusLabels = defaultMissionStatusLabels,
    memberStatusLabels = defaultMemberStatusLabels,
    memberTypeLabels = defaultMemberTypeLabels,
    missionVisibilityLabels = defaultMissionVisibilityLabels,
    resourceTypeLabels = defaultResourceTypeLabels,
    volunteerMissionAffinityLabels = defaultVolunteerMissionAffinityLabels,
    taskTypeLabels = defaultTaskTypeLabels,
    dataProvider = defaultDataProvider,
    authProvider = defaultAuthProvider,
    lightTheme = defaultLightTheme,
    darkTheme = defaultDarkTheme,
    disableTelemetry,
    ...rest
}: CRMProps) => {
    return (
        <ConfigurationProvider
            country={country}
            language={language}
            memberGenderLabels={memberGenderLabels}
            antennaTypes={antennaTypes}
            missionPipelineStatuses={missionPipelineStatuses}
            missionStatusLabels={missionStatusLabels}
            memberStatusLabels={memberStatusLabels}
            memberTypeLabels={memberTypeLabels}
            missionVisibilityLabels={missionVisibilityLabels}
            resourceTypeLabels={resourceTypeLabels}
            volunteerMissionAffinityLabels={volunteerMissionAffinityLabels}
            taskTypeLabels={taskTypeLabels}
            logo={''}
            title={''}
        >
            <Admin
                dataProvider={dataProvider}
                authProvider={authProvider}
                store={localStorageStore(undefined, 'CRM')}
                layout={Layout}
                accessDenied={AccessDeniedPage}
                loginPage={LoginPage}
                dashboard={Dashboard}
                theme={lightTheme}
                darkTheme={darkTheme || null}
                i18nProvider={i18nProvider}
                requireAuth
                disableTelemetry
                {...rest}
            >
                <CustomRoutes noLayout>
                    <Route path={SignupPage.path} element={<SignupPage />} />
                    <Route
                        path={SetPasswordPage.path}
                        element={<SetPasswordPage />}
                    />
                    <Route
                        path={ForgotPasswordPage.path}
                        element={<ForgotPasswordPage />}
                    />
                </CustomRoutes>

                <CustomRoutes>
                    <Route path="/cloud-sync" element={<CloudSyncPage />} />
                </CustomRoutes>

                <Resource name="mission" {...mission} />
                <Resource name="member" {...member} />
                <Resource name="antenna" {...antennas} />
                <Resource name="animation" {...animation} />
                <Resource
                    name="supply"
                    options={{ label: 'Supplies' }}
                    {...supply}
                />
                <Resource name="task" {...tasks} />
                <Resource name="formation" {...formation} />
                <Resource
                    name="expensesClaim"
                    options={{ label: 'Expenses claims' }}
                    {...expensesClaim}
                />
                <Resource
                    name="highlightedResource"
                    options={{ label: 'Highlighted resources' }}
                    {...highlightedResource}
                />
                <Resource
                    name="missionType"
                    options={{ label: 'Mission types' }}
                    {...missionType}
                />
                <Resource
                    name="studyForm"
                    options={{ label: 'Study forms' }}
                    {...studyForm}
                />
                <Resource
                    name="organization"
                    options={{ label: 'Organization settings' }}
                    {...organization}
                />
                <Resource name="tags" list={ListGuesser} />
            </Admin>
        </ConfigurationProvider>
    );
};
